import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { App } from 'app';
import { BusinessLogicProviders } from 'app/components/ReactContexts';
import { FullscreenWordmarkLoader } from 'app/components/loaders';
import { useAuthListener } from 'app/hooks/useAuthListener';
import { InitializationErrorPage } from 'app/pages/InitializationErrorPage';
import { msalInstance } from 'msalConfig/msalConfig';
import React, { useEffect, useState } from 'react';
import { AuthService } from 'services/AuthService';
import { setupAxiosAuthInterceptors } from 'services/api';
import { mobileSettingsApi } from 'services/api/mobile-settings/queries';
import { userInfoApi } from 'services/api/user-info/queries';
import { userSettingsApi } from 'services/api/user-settings/queries';
import { useAppDispatch } from 'services/store.types';
import { FeatureName } from 'types/entities/MobileSettings';

export default function AppInitializer() {
  const dispatch = useAppDispatch();

  const {
    data: mobileSettings,
    isLoading: isSettingsLoading,
    isError: isSettingsError,
  } = mobileSettingsApi.useGetMobileSettingsQuery();

  const [authServiceInstance, setAuthServiceInstance] =
    useState<AuthService | null>(null);
  const isAuthServiceInstantiated = !!authServiceInstance;
  const [isAuthInitError, setIsAuthInitError] = useState(false);

  useEffect(() => {
    if (!mobileSettings || isAuthServiceInstantiated) return;

    const init = async () => {
      try {
        const isSsoEnabled = !mobileSettings.disabledFeatureFlags?.includes(
          FeatureName.SINGLE_SIGN_ON,
        );
        const auth = new AuthService(msalInstance, isSsoEnabled, dispatch);
        setupAxiosAuthInterceptors(auth);
        await auth.handleLoginRedirect();

        if (auth.isAuthenticated()) {
          // To avoid a race condition when retrieving the token, we request it in advance. This can be optimized in the future.
          // Until we figure out why MSAL doesn’t cache the token, we’ll handle it manually.
          await auth.getAccessToken();
        }

        setAuthServiceInstance(auth);
      } catch (e) {
        console.error('System init failed:', e);
        setIsAuthInitError(true);
      }
    };
    init();
  }, [mobileSettings, isAuthServiceInstantiated, dispatch]);

  const isAuthenticated = useAuthListener(authServiceInstance);

  const shouldFetchUser = isAuthenticated && isAuthServiceInstantiated;

  const isSso = authServiceInstance?.isSSOEnabled;

  const userInfoV1 = userInfoApi.useGetUserInfoV1Query(
    shouldFetchUser && !isSso ? undefined : skipToken,
  );

  const userInfoV2 = userInfoApi.useGetUserInfoV2Query(
    shouldFetchUser && isSso ? undefined : skipToken,
  );

  const userSettings = userSettingsApi.useGetUserSettingsQuery(
    shouldFetchUser ? undefined : skipToken,
  );

  const isSystemError =
    isSettingsError ||
    isAuthInitError ||
    (shouldFetchUser && userSettings.isError) ||
    (shouldFetchUser && userInfoV1.isError) ||
    (shouldFetchUser && userInfoV2.isError);

  const isUserLoading =
    shouldFetchUser &&
    (userInfoV1.isLoading || userInfoV2.isLoading || userSettings.isLoading);

  const isSystemLoading =
    !isSystemError &&
    (isSettingsLoading || !authServiceInstance || isUserLoading);

  if (isSystemLoading) {
    return <FullscreenWordmarkLoader />;
  }

  if (isSystemError) {
    return <InitializationErrorPage />;
  }

  // In case the key changes, we guarantee that all components will be unmounted
  // and the Context API will return to its initial state.
  const activeUser = userInfoV2.data || userInfoV1.data;
  const providersKey = activeUser?.email
    ? `user-${activeUser?.email}`
    : 'guest';

  return (
    <BusinessLogicProviders
      key={providersKey}
      authService={authServiceInstance!}
    >
      <App />
    </BusinessLogicProviders>
  );
}
